import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { SessionRecord } from './sessions';

/*
 * Session-note lifecycle API. The server lives under /sessions/:id/*
 * alongside the existing session CRUD — routes land in
 * [server/src/routes/sessions.js]. The React client consumes them
 * exclusively through this module; any component calling `api.patch`
 * directly is a refactor smell.
 */

export type NoteFieldType =
    // Data-capture field types offered by the builder:
    | 'text'
    | 'textarea'
    | 'number'
    | 'date'
    | 'checkbox'
    | 'select'
    | 'multiselect'
    // Display-only section heading (no value, never blocks completion):
    | 'heading'
    // Legacy — still rendered but no longer offered in the builder:
    | 'radio';

export interface NoteField {
    id: string;
    label: string;
    type: NoteFieldType;
    /** Absent treated as `true` — legacy note types preserve their original gate. */
    required?: boolean;
    /** Rendered as a small subline under the label in the note UI. */
    helperText?: string;
    /** Promotes a short-text/number/date field to full-width in the two-column grid. */
    fullWidth?: boolean;
    /** Used by select / multiselect (legacy radio too). */
    choices?: string[];
}

/** Signature row on a note type: 'staff' = staff sig, 'client' = parent sig. */
export interface NoteSignatureDef {
    id: string;
    label: string;
    type: 'staff' | 'client';
    /** Rendered as a small subline under the label in the signature card. */
    helperText?: string;
    /**
     * Legacy per-note-type relationship override. No longer written by
     * the builder — the client signature's relationship picker now always
     * pulls from the practice-wide Parent Relationships list. Kept on
     * the type for read-compat with older JSONB rows.
     */
    relationships?: string[];
}

export interface NoteType {
    id?: string;
    name: string;
    fields: NoteField[];
    signatures: NoteSignatureDef[];
}

export interface NoteFieldValues {
    [fieldId: string]: string | string[] | boolean | number | null;
}

export interface NoteSignatureValue {
    /** base64 data URL of the drawn sig bitmap — empty string means unsigned. */
    drawnPng: string;
    signedAt: string | null;
    signedByUserId: string | null;
    signedByName: string;
    role: string;
}

export interface NoteSignatureValues {
    [sigId: string]: NoteSignatureValue;
}

export interface NoteData {
    fields?: NoteFieldValues;
    signatures?: NoteSignatureValues;
    /** Only present on Finalized notes — frozen copy of the note_type. */
    noteTypeSnapshot?: {
        name: string;
        fields: NoteField[];
        signatures: NoteSignatureDef[];
    };
}

/** Full session + note payload returned by GET /api/sessions/:id. */
export interface SessionDetail extends SessionRecord {
    notes: string;
    noteData: NoteData | null;
    /** Resolved note type — live definition on non-Finalized notes, the
     *  frozen snapshot on Finalized. null when the session has no type. */
    noteType: NoteType | null;
}

export interface FinalizeResponse {
    session: SessionDetail;
    claimId: string | null;
    claimNo: string | null;
}

async function fetchSessionDetail(id: string): Promise<SessionDetail> {
    const res = await api.get<SessionDetail>(`/sessions/${id}`);
    return res.data;
}

export function useSessionDetail(id: string | undefined) {
    return useQuery({
        queryKey: ['session-detail', id] as const,
        queryFn: () => fetchSessionDetail(id!),
        enabled: !!id,
    });
}

export type NoteSaveAction = 'save' | 'complete';

export interface SaveNotePayload {
    noteData: NoteData;
    action: NoteSaveAction;
}

async function saveNote(id: string, payload: SaveNotePayload): Promise<SessionDetail> {
    const res = await api.patch<SessionDetail>(`/sessions/${id}/note`, payload);
    return res.data;
}

export function useSaveNote() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: SaveNotePayload }) => saveNote(id, payload),
        onSuccess: (detail, vars) => {
            qc.setQueryData(['session-detail', vars.id], detail);
            qc.invalidateQueries({ queryKey: ['sessions'] });
        },
    });
}

async function finalizeSession(id: string): Promise<FinalizeResponse> {
    const res = await api.post<FinalizeResponse>(`/sessions/${id}/finalize`);
    return res.data;
}

export function useFinalizeSession() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => finalizeSession(id),
        onSuccess: (result, id) => {
            qc.setQueryData(['session-detail', id], result.session);
            qc.invalidateQueries({ queryKey: ['sessions'] });
            qc.invalidateQueries({ queryKey: ['claims'] });
        },
    });
}

async function sendNoteToPortal(id: string): Promise<SessionDetail> {
    const res = await api.post<SessionDetail>(`/sessions/${id}/note/portal/send`);
    return res.data;
}

export function useSendNoteToPortal() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => sendNoteToPortal(id),
        onSuccess: (detail, id) => {
            qc.setQueryData(['session-detail', id], detail);
            qc.invalidateQueries({ queryKey: ['sessions'] });
        },
    });
}

async function recallNoteFromPortal(id: string): Promise<SessionDetail> {
    const res = await api.post<SessionDetail>(`/sessions/${id}/note/portal/recall`);
    return res.data;
}

export function useRecallNoteFromPortal() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => recallNoteFromPortal(id),
        onSuccess: (detail, id) => {
            qc.setQueryData(['session-detail', id], detail);
            qc.invalidateQueries({ queryKey: ['sessions'] });
        },
    });
}
